import { getRandomValues } from "crypto";
import { parse, isValid } from "date-fns";

import { SHIPPING_METHODS } from "@/lib/constants";
import { getRandomInt } from "@/utils/number";

// Crockfold base32 has 32,768 unique strings for 3 characters
// 32^3 = 32 * 32 * 32 = 1,024 * 32 = 32,768
// Update the number if we have more characters
const MAX_ORDER_IX = 32767;

// Old orderID: 241015A003
export const isOldOrderID = (orderID) => orderID.length === 10;

// Old shippingID: 241015A003R01
export const isOldShippingID = (shippingID) => shippingID.length === 13;

export const canStringBeOrderIDOrShippingID = (text) => {
  // 12 chars: orderID = "241020AAABBB"
  // 16 chars: shippingID = "241020AAABBBR01X"
  return text.length >= 12 && text.length <= 16;
};

export const getOrderIDFromShippingID = (shippingID) => {
  // Old format: 241015A003R01
  if (isOldShippingID(shippingID)) {
    return shippingID.substring(0, 10);
  }

  // New format:
  // shippingID = "241020AAABBBR01X"
  // orderID = "241020AAABBB"
  // 241003A003R01
  return shippingID.substring(0, 12);
};

export const getDateFromOrderIDOrShippingID = (id) => {
  if (!id || typeof id !== "string" || id.length < 6) return null;

  const dateString = id.substring(0, 6);
  const date = parse(dateString, "yyMMdd", new Date());

  if (!isValid(date)) return null;
  return date;
};

export const formatShippingIDReadable = (shippingID) => {
  if (
    // already formatted
    shippingID.includes("-") ||
    isOldShippingID(shippingID)
  )
    return shippingID;

  // Example:
  // shippingID = "241020AAABBBR01"
  // formattedShippingID = "241020-AAABBBR01"
  return shippingID.substring(0, 6) + "-" + shippingID.substring(6);
};

export const formatReadableToShippingID = (formattedShippingID) => {
  // Example:
  // formattedShippingID = "241020-AAABBBR01" or "241020AAABBBR01" or "241015A003R01"
  // shippingID = "241020AAABBBR01"
  return formattedShippingID.replaceAll("-", "").replaceAll("_", "");
};

export const formatOrderIDReadable = (orderID) => {
  if (
    // already formatted
    orderID.includes("-") ||
    // the old format - 241015A003
    isOldOrderID(orderID)
  )
    return orderID;

  // Example:
  // orderID = "241020AAABBB"
  // formattedOrderID = "241020-AAABBB"
  return orderID.substring(0, 6) + "-" + orderID.substring(6);
};

export const generateShippingIdentifiers = ({
  orderID,
  recNumber,
  shipment,
}) => {
  const { shippingMethod } = shipment;
  const recIX = "R" + recNumber.toString().padStart(2, "0");

  // 1-letter shipping method code, either "S", "P" or "X"
  const shippingMethodCode =
    (shippingMethod === SHIPPING_METHODS.STANDARD.value && "S") ||
    (shippingMethod === SHIPPING_METHODS.POSTAL.value && "P") ||
    "X";

  let shippingID;

  if (isOldOrderID(orderID)) {
    shippingID = orderID + recIX;
  } else {
    shippingID = orderID + recIX + shippingMethodCode;
  }

  return {
    shippingID,
    recIX,
  };
};

export const intToCrockfordBase32 = (num) => {
  if (num < 0 || num > MAX_ORDER_IX) {
    throw new RangeError(`Input must be between 0 and ${MAX_ORDER_IX}`);
  }

  // Crockford base32 characters
  const crockfordChars = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
  let result = "";

  // Convert the number to base32
  for (let i = 0; i < 3; i++) {
    result = crockfordChars[num % 32] + result;
    num = Math.floor(num / 32);
  }

  return result;
};

export const getRandomAlphanumericString = (length) => {
  // Crockford base32 characters
  const chars = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
  let result = "";
  const randomArray = new Uint8Array(length);
  getRandomValues(randomArray);
  randomArray.forEach((number) => {
    result += chars[number % chars.length];
  });
  return result;
};

export const generateRandomOrderIX = () => {
  return getRandomInt(0, MAX_ORDER_IX);
};

export const generateOrderID = ({ orderIX, submitDate }) => {
  // Example:
  // orderIX = 12345
  // submitDate = "241020" (20 Oct 2024)
  // orderID = "241020AAABBB", where AAA is an incremental string offset by orderIX, and BBB is a random string
  //
  // Reference: Proposal 1C
  // https://www.notion.so/justship/JustShip-Order-and-Shipment-ID-c8243675c1be4f2789074896addf5e0e

  return (
    submitDate + intToCrockfordBase32(orderIX) + getRandomAlphanumericString(3)
  );
};

export const generateDiscountCode = () => {
  const timestamp = Date.now().toString(36);
  const randomString = getRandomAlphanumericString(6);
  return (timestamp + randomString).toLowerCase();
};
